
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { CheckCircle2, ArrowRight } from "lucide-react";
import PriceDisplay from "@/components/ui/price-display";

interface PledgeTierData {
  id: string;
  name: string;
  amount: number;
  regularPrice: number;
  description: string;
  features: string[];
  discount: string;
  savings: number;
}

interface PledgeTierProps {
  tier: PledgeTierData;
  isProcessing: boolean;
  loading: boolean;
  onPledgeClick: (tierId: string) => void;
}

const PledgeTier = ({ tier, isProcessing, loading, onPledgeClick }: PledgeTierProps) => {
  return (
    <Card 
      className={`bg-background border-border/40 hover:shadow-md transition-all ${
        isProcessing ? 'border-primary ring-1 ring-primary/30' : ''
      } flex flex-col relative`}
    >
      <CardContent className="p-6 pt-8 flex-grow">
        <div className="text-center mb-6">
          <h3 className="text-xl font-medium mb-2">{tier.name}</h3>
          <div className="flex justify-center mb-1">
            <PriceDisplay amount={tier.amount} size="md" />
          </div>
          <p className="text-xs text-muted-foreground mt-1 mb-2">{tier.description}</p>
          <div className="flex flex-col gap-1 items-center">
            <span className="inline-block text-xs font-medium bg-green-100 text-green-800 px-2 py-1 rounded-full">
              {tier.discount} • Save ${tier.savings}
            </span>
            <span className="text-xs text-muted-foreground line-through">
              Regular: ${tier.regularPrice}
            </span>
          </div>
        </div>
        
        <div className="space-y-1 text-left">
          {tier.features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 py-2 border-t border-border/20 first:border-t-0">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0" />
              <span className="text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </CardContent>
      
      <CardFooter className="p-6 pt-0 flex justify-center">
        <Button 
          className="w-full" 
          onClick={() => onPledgeClick(tier.id)}
          disabled={loading}
        >
          {isProcessing ? (
            "Processing..."
          ) : (
            <>
              Pledge Now
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PledgeTier;
